import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import classes from 'app/components/DataGrid/classes.module.scss';
import useGuests from 'app/hooks/useGuests';
import useDeeplink from 'app/hooks/useDeeplink';
import GuestModal from './GuestModal';
import MomentsCell from 'app/components/DataGrid/MomentsCell';
import GuestUploadProgressBar from './GuestUploadProgressBar';

import {
  getGuest,
  getGuests as exportGuestsSvc
} from 'app/services/guests';

import {
  getSimpleJobs
} from 'app/services/jobs';

import {
  getKiosks
} from 'app/services/kiosks';

import {
  setPhotoSwipeOpen,
  setPhotoSwipeMoment
} from 'app/actions/photoSwipeActions';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  moments as momentsPath
} from 'app/routes';

import {
  formatJobSelectLabel,
  formatJobOptionLabel,
  formatJobCellValue,
  //formatJobOptions
} from 'app/utils/valueFormatters';

import {
  createOption,
  formatOptions
} from 'app/utils/options';

import {
  moments
} from 'app/resources';

const Guests = () => {
  const account = useSelector(state => state.portalReducer.account);

  const [kioskOptions, setKioskOptions] = useState([]);
  const loadKioskOptions = async () => {
    let kiosks = await getKiosks(account.id);
    setKioskOptions(formatOptions(kiosks));
  }
  useEffect(() => {
    if (account?.feature_flags.kiosk_mode) {
      loadKioskOptions();
    }
  }, []);

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData,
    updateRow,
    getFilterModel
  } = useGuests();

  const exportCSV = () => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportGuestsSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `guests_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const deeplink = useDeeplink();

  const showAccessCode = !account.feature_flags.hide_access_code;

  const getJobOptions = async (page = 1, perPage = 100, filter = null) => {
    let jobs = await getSimpleJobs();
    return formatOptions(jobs);
  }

  // todo: put in shared spot
  const statusOptions = [
    createOption('Created', 0),
    createOption('Preparing Invite', 1),
    createOption('Invite Sent', 2),
    createOption('Viewed', 3),
    createOption('Invite Failed', 4)
  ]

  const viewMomentsForGuest = async storyId => {
    // filter moments by guest ahead of showing the moments table.
    // this will overwrite any filters previously set.
    // filter moments by guest ahead of showing the moments table.
    // this will overwrite any filters previously set.

    let filterModel = {
      story_id: [{
        filterClass: 'text',
        filterType: 'equals',
        value: storyId.toString(),
        operator: AND_OPERATOR
      }]
    }

    deeplink.load(moments, filterModel);
  }

  const header = 'Guests';

  let columnOrder = [
    'moments',
    'upload_status',
    'name',
    'email',
    'room',
    'check_in',
    'check_out',
    'kiosk_id'
  ]

  let visibleColumns = [].concat(columnOrder);

  let omitColumns = ['job'];
  if (!showAccessCode) {
    omitColumns.push('access_code');
  }

  const onMomentClick = moment => {
    dispatch(setPhotoSwipeMoment(moment));
    dispatch(setPhotoSwipeOpen(true));
  }

  const setPhotoSwipeItem = guest => {
    if (guest.cover_moment) {
      return {
        id: guest.cover_moment.id,
        src: guest.cover_moment.preview_url,
        w: guest.cover_moment.width,
        h: guest.cover_moment.height,
        digital_edit: guest.cover_moment.digital_edit,
        orig_moment_id: guest.cover_moment.orig_moment_id,
      }
    }
  };

  // setup callbacks for uploads via both modal and grid cell
  const onUploadStart = (upload, { customer }) => {
    console.log(customer);
    customer.last_upload = upload;
    updateRow(customer);
  }

  const onUploadComplete = async (upload, { customer }) => {
    let updatedGuest = await getGuest(customer.id);
    updatedGuest.last_upload = upload;
    updateRow(updatedGuest);
  }

  const columnDefOverrides = [
    {
      field: 'moments',
      headerName: 'Moments',
      pinned: 'left',
      inputType: 'number',
      width: 11 * 16,
      thumbnail: data => data && data.cover_moment && data.cover_moment.thumb_url,
      onThumbnailClick: data => onMomentClick(data.cover_moment),
      cellRenderer: params => {
	const {
          data,
	  colDef
	} = params;

        if (data) {
          const {
	    id,
	    photo_count,
	    job_id,
	    story_id
	  } = data;

          const onImagesClick = () => viewMomentsForGuest(story_id);

	  // disabling the upload label for now,
	  // as uploads from here are disabled
	  let showUploadLabel = false;
	  // only show UPLOAD label if no images
	  //let showUploadLabel = !photo_count;

	  return (
            <MomentsCell
              data={data}
              colDef={colDef}
              showImageCount={true}
              imageCount={photo_count}
              onImagesClick={onImagesClick}
              showUpload={false}
              showUploadLabel={showUploadLabel}
              uploadJobId={job_id}
              uploadGuestId={id}
              uploadStoryIds={[story_id]}
              onUploadStart={onUploadStart}
              onUploadComplete={onUploadComplete}
              customer={data}
            />
	  );
        }
        return '';
      }
    },
    {
      field: 'upload_status',
      headerName: 'Last Upload / Status',
      //pinned: 'left',
      width: 220,
      cellRenderer: params => {
	let { data } = params;
        let upload = data?.last_upload;
        if (upload) {
          if (upload.completed_at) {
            return <span className={classes.gridCell}>Complete</span>;
          } else {
        return <GuestUploadProgressBar guest={data} />;
          }
        }
        return '';
      }
    },
    {
      field: 'kiosk_id',
      headerName: 'Kiosk',
      minWidth: 250,
      dataValueFormatter: (data, value) => {
        return formatJobCellValue(data.kiosk);
      },
      quickEdit: true,
      inputType: 'select',
      inputOptions: kioskOptions
    },
    { field: 'name', quickEdit: true },
    { field: 'email', quickEdit: true },
    { field: 'phone', quickEdit: true },
    { field: 'access_code', quickEdit: true },
    {
      field: 'notify_count',
      headerName: 'Invites Sent',
      inputType: 'number'
    },
    {
      field: 'status',
      inputType: 'select',
      inputOptions: statusOptions,
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          const { status_id } = data;
          let status = statusOptions.find(opt => opt.value == status_id);
          status &&= status.label;

          return (
            <div className={classes.gridCell}>
              {status}
            </div>
          );
        }
        return null;
      }
    },
    { field: 'id', minWidth: 10 * 16 },
    { field: 'check_in', formatType: 'date', inputType: 'date' },
    { field: 'check_out', formatType: 'date', inputType: 'date' }
  ]

  const newRowTemplate = {
    // removed jobs from Redux, will need to figure this out
    job_id: null //jobs && jobs[0] && jobs[0].id
  }

  const onNotify = records => {

  }

  const actions = [
    { key: 'notify', label: 'Notify Guests', callback: onNotify },
    { key: 'destroy', label: 'Delete Guests', callback: onDestroy },
  ]

  const buttons = [
    { key: 'export', icon: <FileDownloadIcon />, label: 'CSV', onClick: exportCSV }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        showAdd={false}
        showSelect={true}
        showEdit={true}
        onEdit={onEdit}
        quickEditEnabled={false}
        onQuickAdd={onQuickAdd}
        onQuickEdit={onQuickEdit}
        newRowTemplate={newRowTemplate}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        omitColumns={omitColumns}
        columnDefOverrides={columnDefOverrides}
        setPhotoSwipeItem={setPhotoSwipeItem}
        showFilters={showFilters}
        actions={actions}
        loadingMessage="Loading guests..."
        onDataGridLoad={onDataGridLoad}
        buttons={buttons}
      />
      <GuestModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        guest={currentRecord}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
        onUploadStart={onUploadStart}
        onUploadComplete={onUploadComplete}
        kioskOptions={kioskOptions}
      />
    </div>
  );
}

export default Guests;
